import { API_BASE } from './uploadWithProgress';

/**
 * Three-step direct-to-R2 upload:
 *   1. POST /api/uploads/presign        → { uploadUrl, key }
 *   2. PUT  uploadUrl  (browser → R2)   → real upload progress (XHR)
 *   3. POST /api/uploads/finalize       → backend writes DB, enqueues compression
 *
 * Resolves with: { key, publicUrl, videoJobId | null }
 *
 * Why this beats buffering through Express:
 *   - Browser sees ACCURATE upload progress (browser → R2, not browser → API).
 *   - The API server doesn't hold a 500MB Buffer in RAM per upload.
 *   - Compression happens out-of-band in a worker process, so users don't wait.
 */

function getToken() {
  return localStorage.getItem('accessToken') || '';
}

async function postJSON(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw Object.assign(new Error(data?.message || 'Request failed'), { status: res.status, data });
  }
  return data;
}

function putToR2({ url, file, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(Object.assign(new Error(`R2 PUT failed: ${xhr.status}`), { status: xhr.status }));
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.send(file);
  });
}

/**
 * @param {Object} args
 * @param {File} args.file
 * @param {'lesson_video'|'preview_video'|'cover_image'|'avatar'} args.kind
 * @param {string=} args.courseId
 * @param {string=} args.lessonId
 * @param {number=} args.durationMinutes  (only for videos)
 * @param {(pct:number)=>void=} args.onProgress
 * @param {AbortSignal=} args.signal
 */
export async function uploadDirect({
  file,
  kind,
  courseId,
  lessonId,
  durationMinutes,
  onProgress,
  signal,
}) {
  if (!file) throw new Error('file required');

  // 1. Presign
  const { uploadUrl, key } = await postJSON('/uploads/presign', {
    kind,
    courseId,
    lessonId,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type || 'application/octet-stream',
  });

  // 2. Upload directly to R2
  await putToR2({ url: uploadUrl, file, onProgress, signal });

  // 3. Finalize — backend updates DB, enqueues compression
  const finalizeRes = await postJSON('/uploads/finalize', {
    kind,
    key,
    courseId,
    lessonId,
    originalName: file.name,
    mimeType: file.type,
    size: file.size,
    durationMinutes,
  });

  return {
    key,
    publicUrl: finalizeRes.publicUrl,
    videoJobId: finalizeRes.videoJobId || null,
  };
}

/**
 * Poll a video-compression job until it reaches a terminal state.
 * Calls onProgress(jobObject) on each tick.
 */
export async function pollVideoJob({ jobId, onProgress, intervalMs = 2000, timeoutMs = 30 * 60 * 1000 }) {
  const start = Date.now();
  while (true) {
    const res = await fetch(`${API_BASE}/uploads/jobs/${jobId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw Object.assign(new Error(data?.message || 'Job poll failed'), { status: res.status });
    }
    const data = await res.json();
    if (onProgress) onProgress(data.job);
    if (data.job.status === 'ready' || data.job.status === 'failed') return data.job;
    if (Date.now() - start > timeoutMs) {
      throw new Error('Job poll timeout');
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
}

/**
 * Найти АКТИВНЫЙ (queued/processing) джоб сжатия для урока.
 * Используется при mount LessonItem'а: если был refresh страницы пока шло
 * сжатие — фронт должен подцепиться к существующему джобу, а не показывать
 * «всё готово» пока на бэке ещё работает ffmpeg.
 *
 * @returns {Promise<object|null>} объект job или null если активного нет
 */
export async function fetchActiveJobForLesson(lessonId) {
  if (!lessonId) return null;
  try {
    const res = await fetch(`${API_BASE}/uploads/jobs/by-lesson/${lessonId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.job || null;
  } catch {
    // Сеть упала / 404 — спокойно возвращаем null, lesson просто покажет статус
    // на основе того, есть videoKey или нет.
    return null;
  }
}
